/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-use-before-define */
/* eslint-disable max-lines-per-function */
import { type ECharts, type SeriesModel } from 'echarts';
import { util, graphic } from 'echarts/core';
import type { ZRStyleProps } from 'echarts/types/src/util/types';
import type Element from 'zrender/lib/Element.js';
import type { ElementState } from 'zrender/lib/Element.js';
import type StandardChart from '../../core/StandardChart';
import type { ElementAnimateConfig } from '../../index';
import { isArray } from '../../util/utils';
import { BreathCircle } from './BreathPoint';
import { Line } from './VerticalPointer';

export type ModelAndView = {
  model: SeriesModel;
  view: unknown;
};

export type TimeLineHelperInitCfg = {
  // 传参 更新时间间隔
  duration?: number;
  delay?: number;
  dataIndex?: number;
  breathPoint?: {
    show?: boolean;
    shape?: {
      r?: number;
    };
    style?: ZRStyleProps;
    z: number;
  };
  verticalPointer?: {
    show?: boolean;
    style?: ZRStyleProps;
  };
  endLabel?: {
    containLabel?: boolean;
  };
  dvDuring?: (
    percent: number,
    lineClipPath: Element,
    lineModelAndView: ModelAndView,
    lineModelsAndViews: ModelAndView[]
  ) => void;
};

export type BoundingRect = { x: number; y: number; width: number; height: number };
//  ------------------------------------------------------------------------------------------------//
/**
 * 调整末尾标签动画过程中每一帧的位置，只在单个裁切矩形状态变化的回调函数中调用
 * @todo 现在是在任意一个折线的裁切矩形的状态转移动画的回调中都进行调用，性能上有很大的优化空间。
 * @param gridRect 折线对应的grid的宽高矩形
 * @param endLabel 折线对应的末尾标签的zr对象
 * @param lineModelsAndViews 图表全局series的{model，view}数组
 */
function adjustEndLablePosition(
  gridRect: BoundingRect,
  lineModelsAndViews: ModelAndView[],
  containLabel: boolean
) {
  const { width: gridWidth, x: gridX, y: gridY, height: gridHeight } = gridRect;

  // 末尾标签到grid左右的间距
  const gridRightPadding = 6;
  const gridTopPadding = 2;
  // 末尾标签之间、末尾标签与grid上下的间隙
  const endLabelDistance = 2;
  // 末尾标签和数据点坐标的偏移 todo

  lineModelsAndViews;
  const endLabelParamsList = lineModelsAndViews
    .filter(modelAndView => {
      return modelAndView.view?._endLabel;
    })
    .map((element, idx) => {
      const { view } = element;
      const { x: endLabelX, y: endLabelY } = view?._endLabel as graphic.Text;

      const { width: endLabelWidth, height: endLabelHeight } = (
        view?._endLabel as graphic.Text
      )?.getBoundingRect();

      const adjustX =
        endLabelX + endLabelWidth > gridWidth + gridX - gridRightPadding
          ? gridWidth + gridX - gridRightPadding - endLabelWidth
          : endLabelX;
      const adjustY =
        endLabelY > gridY + gridTopPadding
          ? endLabelY + endLabelHeight < gridY + gridHeight - gridTopPadding
            ? endLabelY
            : gridY + gridHeight - gridTopPadding
          : gridY + gridTopPadding;
      return {
        x: containLabel ? adjustX : endLabelX,
        y: endLabelY,
        pointY: adjustY,
        width: endLabelWidth,
        height: endLabelHeight,
        zr: view?._endLabel as Element
      };
    })
    .sort((a, b) => a.y - b.y);

  if (endLabelParamsList.length < 2) {
    endLabelParamsList.forEach(i => {
      i.zr.attr({
        x: i.x,
        y: i.pointY
      });
    });
    return;
  }

  // 图表上界
  const upperBound = gridY + gridTopPadding;
  // 图表下界
  const lowerBound = gridY + gridHeight - gridTopPadding;

  let lastPos = gridY;
  let delta = 0;
  const len = endLabelParamsList.length;

  for (let i = 0; i < len; i += 1) {
    const item = endLabelParamsList[i];
    delta = item.y - lastPos;
    if (delta < 0) {
      item.y -= delta;
    }
    lastPos = item.y + item.height + endLabelDistance;
  }

  const first = endLabelParamsList[0];
  const last = endLabelParamsList[len - 1];
  let upGap: number;
  let downGap: number;

  updateUpDownGap();
  takeBoundsGap(upGap, downGap, 1);
  takeBoundsGap(downGap, upGap, -1);

  /**
   * 对list的[start, end)区间内的label进行偏移量为delta的调整
   * @param delta 偏移量
   * @param start 开始偏移的label索引 （包含start）
   * @param end 结束偏移的label索引 （不包含end）
   */
  function shiftList(dlt: number, start: number, end: number) {
    // 遍历区间[start, end)并对其中每一个label进行偏移
    for (let i = start; i < end; i += 1) {
      endLabelParamsList[i].y += dlt;
    }
  }

  /**
   * 更新上界距离和下界距离
   */
  function updateUpDownGap() {
    // 上界距离为列表首位label包围盒的上边界到上界参数的差, 由于首位label垂直居中所以减去height / 2
    upGap = first.y - upperBound - first.height / 2;
    // 下界距离为下界参数到列表末尾label包围盒的下边界的差
    downGap = lowerBound - last.y - last.height;
  }

  /**
   * 压缩label列表之间的间隙
   * @param dlt 这里的delta应该只起到标记判断的作用
   * @param maxSqeezePercent  设定压缩最大百分比阈值
   * @returns
   */
  function squeezeGaps(dlt: number, maxSqeezePercent: number) {
    // 记录列表中相邻label间的间距
    // gaps[i]表示list[i + 1]距离前一个label的距离
    const gaps: number[] = [];
    // 记录label间的总间距
    let totalGaps = 0;
    for (let i = 1; i < len; i++) {
      // 获取上一个label的包围盒模型
      const prevItemRect = endLabelParamsList[i - 1];
      // 计算当前label与前一个label的间距，若存在重叠（某单一维度相切）则取0
      const gap = Math.max(endLabelParamsList[i].y - prevItemRect.y - prevItemRect.height, 0);
      // 存入gaps
      gaps.push(gap);
      // 更新总间距
      totalGaps += gap;
    }
    // 如果当前列表所有相邻label都没有间距，则不再收缩
    if (!totalGaps) {
      return;
    }

    // 处理收缩百分比，
    const squeezePercent = Math.min(Math.abs(dlt) / totalGaps, maxSqeezePercent);

    // 如果delta大于零 遍历每一个label
    // gap[i]对应的list[i + 1]，将每个list[i + 1]前面的label ==>区间[0, i + 1)里的label都往下（右）偏移gaps[i] * precent
    if (dlt > 0) {
      for (let i = 0; i < len - 1; i++) {
        // Distribute the shift delta to all gaps.
        const movement = gaps[i] * squeezePercent;
        // Forward
        shiftList(movement, 0, i + 1);
      }
    }
    // 如果delta小于0，则方向变为向上（左） 顺序变为自下而上（自右向左）偏移量为gaps[i] * percent
    else {
      // Backward
      for (let i = len - 1; i > 0; i--) {
        // Distribute the shift delta to all gaps.
        const movement = gaps[i - 1] * squeezePercent;
        shiftList(-movement, i, len);
      }
    }
  }

  /**
   * 将label列表整体移入图表内部， 保证不出界
   * @param gapThisBound
   * @param gapOtherBound
   * @param moveDir
   */
  function takeBoundsGap(gapThisBound: number, gapOtherBound: number, moveDir: -1 | 1) {
    // 如果当前一端超出界限
    if (gapThisBound < 0) {
      // 尽可能向另一端偏移以实现列表移入图表中
      const moveFromMaxGap = Math.min(gapOtherBound, -gapThisBound);
      // 如果另一端间距大于0, 则moveFromMaxGap为gapOtherBound和-gapOtherBound的较小值。
      if (moveFromMaxGap > 0) {
        // 向moveDir方向移动moveFromMaxGap偏移值
        shiftList(moveFromMaxGap * moveDir, 0, len);
        const remained = moveFromMaxGap + gapThisBound;
        // 如果整体偏移值moveFromMaxGap不够将label列表移回上下界内部, 则再次将列表label向moveDir方向压缩全部间隙
        if (remained < 0) {
          squeezeGaps(-remained * moveDir, 1);
        }
      } else {
        // 如果另一端间距小于等于0, 则将列表label向moveDir方向压缩全部间隙
        squeezeGaps(-gapThisBound * moveDir, 1);
      }
    }
  }

  // 对视图层进行图像上的修改
  endLabelParamsList.forEach(i => {
    i.zr.attr({
      x: i.x,
      y: i.y
    });
  });
}

function getClipStates(points: number[], start: number) {
  const states = {};
  for (let i = 0; i < points.length; i += 2) {
    const tmp = {
      shape: {
        width: points[i] - start
      },
      dvPointY: points[i + 1]
    };
    states[i / 2] = util.clone(tmp);
  }
  return states;
}

function clipOnDuring(
  lineClipPath: Element,
  lineModelAndView: ModelAndView,
  lineModelsAndViews: ModelAndView[],
  last: boolean,
  containLabel,
  dvDuring?: (...args: Parameters<TimeLineHelperInitCfg['dvDuring']>) => void
) {
  const { model, view } = lineModelAndView;
  const data = model.getData();
  const endLabelModel = model.getModel('endLabel');
  const coordSys = model.coordinateSystem;
  const labelAnimationRecord = { lastFrameIndex: 0 };
  // todo lineModel依赖的axisModel类型转换为Axis2D

  // 将所有折线的modelandview按照所依赖的grid划分
  // 并且记录map和key值， 用于不同grid下的endLabel位置调整.
  // MAV: ModelAndView
  const lineMAVMap = {};
  const lineMAVKeyArr = [];
  lineModelsAndViews.forEach(item => {
    const { model: lModel } = item;
    if (!lModel.option.endLabel.show) {
      return;
    }
    const gridId = lModel.coordinateSystem.model.uid;
    if (!isArray(lineMAVMap[gridId])) {
      lineMAVKeyArr.push(gridId);
      lineMAVMap[gridId] = [];
    }
    lineMAVMap[gridId].push(item);
  });

  return (percent: number) => {
    if (endLabelModel.option.show) {
      // 实时改变endLabel的数值， 形成数值上的过渡动画
      view?._endLabelOnDuring(
        percent,
        lineClipPath,
        data,
        labelAnimationRecord,
        true,
        endLabelModel,
        coordSys
      );

      // 上一状态或下一状态数据异常，则隐藏endLabel和呼吸点
      const lastState: ElementState = lineClipPath.getState(
        lineClipPath.extra.lastStateName as string
      );
      const [currentStateName] = lineClipPath.currentStates;
      const currentState: ElementState = lineClipPath.getState(currentStateName);
      if (
        !isNaN(lineClipPath.getState(lineClipPath.currentStates[0]).dvPointY) &&
        isNaN(lastState.dvPointY)
      ) {
        const points = data.getLayout('points') as number[];
        const curIndex = parseInt(currentStateName, 10);
        const curPoint = [points[curIndex * 2], points[curIndex * 2 + 1]];
        view?._endLabel?.attr({
          x: curPoint[0],
          y: curPoint[1]
        });
      }
      if (percent === 0) {
        view?._endLabel?.attr({
          ignore: isNaN(currentState.dvPointY)
        });
        view.breathCircle.attr({
          ignore: isNaN(currentState.dvPointY)
        });
      }
      const { x, y } = view?._endLabel as Element;
      const circleX = lineClipPath.shape.x + lineClipPath.shape.width;

      if (view.breathCircle) {
        view.breathCircle.move([circleX, y]);
        view.breathCircle.breath(true);
      }
      if (view.verticalPointer) {
        view.verticalPointer.move([circleX, y]);
      }

      if (last) {
        lineMAVKeyArr.forEach(key => {
          // 获取同一grid的末尾标签的grid的包围盒数据，因为都是同一个grid，所以默认取了第一个
          const gridRect = lineMAVMap[key][0].model.coordinateSystem.master.getRect();
          adjustEndLablePosition(gridRect, lineMAVMap[key], containLabel);
        });
      }
    }
    // 自定义帧回调
    if (util.isFunction(dvDuring)) {
      dvDuring(percent, lineClipPath, lineModelAndView, lineModelsAndViews);
    }
  };
}

export default class LineTimeLineHelper {
  // standard-chart图表实例
  private _Chart: StandardChart;

  // _echart实例
  private myChart: ECharts;

  // 时间轴动画配置
  private stateTransitionConfig: ElementAnimateConfig & TimeLineHelperInitCfg;

  // 折线的model和view数组
  lineModelsAndViews: ModelAndView[];

  // 传参 控制呼吸点和末尾标签和竖直光标的动画
  constructor(chart: StandardChart, transitionConfig?: TimeLineHelperInitCfg) {
    this._Chart = chart;
    this.myChart = chart.getECharts();
    this.stateTransitionConfig = util.merge(transitionConfig || {}, {
      duration: 250,
      delay: 0,
      dataIndex: 0,
      breathPoint: {
        show: true,
        shape: {
          r: 3
        },
        style: {
          fill: 'inherit'
        },
        z: 0
      },
      endLabel: {
        containLabel: true
      },
      verticalPointer: {
        show: true,
        style: {
          color: 'inherit',
          lineDash: [2, 2]
        }
      }
    });
    this._init();
  }

  _init() {
    const { myChart, stateTransitionConfig } = this;
    this.lineModelsAndViews = myChart
      .getModel()
      .getSeriesByType('line')
      .map((model: SeriesModel) => {
        return {
          model,
          view: myChart.getViewOfSeriesModel(model)
        };
      });
    // todo 如果lineModelsAndViews为空 continue
    this.prepareClip();
    this.start(stateTransitionConfig.dataIndex);
  }

  prepareClip() {
    const { lineModelsAndViews, myChart, stateTransitionConfig } = this;
    const { breathPoint, verticalPointer, endLabel } = stateTransitionConfig;
    const zr = myChart.getZr();
    const len = lineModelsAndViews.length;
    lineModelsAndViews.forEach((element, index) => {
      const { view, model } = element;
      // 获取当前末尾标签是否显示
      const endLabelShow = model.getModel('endLabel').option.show;
      // 获取裁切矩形的zr实例
      const lineClipPath = view._lineGroup.getClipPath() as Element;
      // 从全局动画中删除原动画的动画器
      if (lineClipPath.animators[0]) {
        myChart.getZr().animation.removeAnimator(lineClipPath.animators[0]);
      }
      // 根据配置配置呼吸点和光标
      if (view.breathCircle) {
        view.breathCircle.remove();
      }
      if (breathPoint.show) {
        const style = util.clone(breathPoint.style);
        const color =
          breathPoint.style.fill === 'inherit'
            ? model.option.itemStyle.color
            : breathPoint.style.fill;
        style.fill = color;

        if (endLabelShow) {
          view.breathCircle = new BreathCircle(zr, myChart, {
            r: breathPoint.shape.r,
            style,
            z: breathPoint.z
          });
          view._lineGroup.add(view.breathCircle.circle);
          view._lineGroup.add(view.breathCircle.shadow);
        }
      }
      if (verticalPointer.show && !index) {
        if (!view.verticalPointer) {
          view.verticalPointer = new Line(zr, model, {
            style: verticalPointer.style
          });
        }
      }
      // 获取对应折线的全部状态，对于没有数据的点
      const points = view._points;
      const start = view._lineGroup.getClipPath().shape.x as number;
      const states = getClipStates(points, start);
      // 将不同状态对应数据的添加到clip的zr实例的状态列表中
      lineClipPath.states = util.clone(states);
      // 为每个clip实例设置状态转移的动画参数
      lineClipPath.stateTransition = util.merge(
        {
          during: clipOnDuring(
            lineClipPath,
            element,
            lineModelsAndViews,
            index === len - 1,
            endLabel.containLabel,
            stateTransitionConfig.dvDuring
          ),
          force: true,
          done: () => {
            const [stateName] = lineClipPath.currentStates;
            const state = lineClipPath.getState(stateName);
            if (endLabelShow) {
              view?._endLabel?.attr({
                ignore: isNaN(state.dvPointY)
              });
              if (view.breathCircle) {
                view.breathCircle.attr({
                  ignore: isNaN(state.dvPointY)
                });
              }
            }
            lineClipPath.extra.lastStateName = stateName;
          },
          aborted: () => {
            const [stateName] = lineClipPath.currentStates;
            const state = lineClipPath.getState(stateName);
            if (endLabelShow) {
              view?._endLabel?.attr({
                ignore: isNaN(state.dvPointY)
              });
              if (view.breathCircle) {
                view?.breathCircle.attr({
                  ignore: isNaN(state.dvPointY)
                });
              }
            }
            lineClipPath.extra.lastStateName = stateName;
          }
        },
        stateTransitionConfig
      );
      lineClipPath.extra = {
        lastStateName: '0'
      };
    });
  }

  start(dataIndex: number) {
    const { lineModelsAndViews } = this;
    lineModelsAndViews.forEach(element => {
      const lineClipPath = element.view._lineGroup.getClipPath() as Element;
      lineClipPath.useState(`${dataIndex}`);
      lineClipPath.stopAnimation(undefined, true);
    });
  }

  change(index: number, noAnimation = false) {
    const { lineModelsAndViews } = this;
    lineModelsAndViews.forEach(element => {
      const lineClipPath = element.view._lineGroup.getClipPath() as Element;
      const stateName = `${index}`;
      if (element.view?._endLabel == null) {
        return;
      }
      if (lineClipPath.getState(stateName)) {
        lineClipPath.useState(`${index}`, false, false);

        if (noAnimation) {
          // 保留动画的帧回调，但是不要状态转移的动画过程
          lineClipPath.stopAnimation(undefined, true);
        }
      } else if (element.view?._endLabel) {
        // 如果echarts内部变更了视图层元素（替换对象等），则同步更新helper
        const newHelper = new LineTimeLineHelper(this._Chart, this.stateTransitionConfig);
        newHelper.change(index, noAnimation);
        return newHelper;
      } else {
        console.error(`No state ${stateName}`, element.model.get('name'));
      }
    });
  }

  setTransitionConfig(config: TimeLineHelperInitCfg) {
    const { stateTransitionConfig, lineModelsAndViews } = this;
    this.stateTransitionConfig = util.merge(config, stateTransitionConfig);
    lineModelsAndViews.forEach((modelAndView: ModelAndView) => {
      const { view } = modelAndView;
      // 获取裁切矩形的zr实例
      const lineClipPath = view._lineGroup.getClipPath() as Element;
      lineClipPath.stateTransition = util.merge(
        lineClipPath.stateTransition,
        this.stateTransitionConfig,
        true
      );
    });
  }
}
